import { useEffect, useState } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import {
    ORDER_ONLINE_URL,
    MONOGRAM_URL,
    FACEBOOK_URL,
    MAPS_URL,
} from "../lib/data";

const links = [
    { label: "Home", to: "/" },
    { label: "Menu", to: "/menu" },
    { label: "Book", to: "/book" },
    { label: "Vouchers", to: "/vouchers" },
    { label: "About", to: "/about" },
];

// Reusable brand mark: monogram + "THE GAFFER" wordtype.
// The monogram PNG ships with an off-white background matching the page
// background, so it blends naturally — no filter or blend mode needed.
function BrandMark({ size = "default" }) {
    const sizes = {
        sm: { img: "h-10 md:h-8", text: "text-[1.55rem] md:text-xl" },
        default: { img: "h-11 md:h-11", text: "text-2xl md:text-2xl" },
        lg: { img: "h-12 md:h-10", text: "text-3xl md:text-2xl" },
    };
    const s = sizes[size] || sizes.default;
    return (
        <span className="inline-flex items-center gap-2.5">
            <img
                src={MONOGRAM_URL}
                alt=""
                aria-hidden="true"
                className={`${s.img} w-auto object-contain select-none pointer-events-none`}
            />
            <span
                className={`font-display uppercase tracking-tight text-[#121212] leading-none ${s.text}`}
            >
                The Gaffer
            </span>
        </span>
    );
}

export default function Navigation() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const isHome = pathname === "/";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 16);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Close mobile menu whenever the route changes
    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    // Lock body scroll when mobile menu is open
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    const handleBack = () => {
        if (window.history.state && window.history.state.idx > 0) {
            navigate(-1);
        } else {
            navigate("/");
        }
    };

    // Always scroll to top when a header/menu link is clicked — works even
    // when the user clicks the route they are already on (no path change,
    // ScrollToTop wouldn't fire), and always closes the mobile overlay.
    const handleNavClick = () => {
        setOpen(false);
        if (window.__lenis && typeof window.__lenis.scrollTo === "function") {
            window.__lenis.scrollTo(0, { immediate: true, force: true });
        }
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    };

    return (
        <>
            <header
                data-testid="main-nav"
                style={{ isolation: "isolate" }}
                className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                    scrolled || !isHome
                        ? "bg-[#FAF8F4]"
                        : "bg-transparent"
                }`}
            >
                <div className="relative mx-auto max-w-[1400px] px-4 md:px-10 lg:px-14 h-20 flex items-center justify-between">
                    {/* LEFT — back (mobile sub-page) OR logo */}
                    <div className="flex items-center">
                        {!isHome && (
                            <button
                                type="button"
                                onClick={handleBack}
                                data-testid="nav-back-btn"
                                aria-label="Go back"
                                className="md:hidden -ml-2 p-2 text-[#121212] hover:text-[#EB4A18] transition-colors duration-200"
                            >
                                <svg
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="w-10 h-10"
                                >
                                    <path d="M19 12H5M12 19l-7-7 7-7" />
                                </svg>
                            </button>
                        )}
                        {/* Logo: hidden on mobile sub-pages (centered instead), visible on home mobile + always on desktop */}
                        <Link
                            to="/"
                            data-testid="nav-wordmark"
                            aria-label="The Gaffer Burgers & Shakes — Home"
                            className={`flex items-center ${
                                !isHome ? "hidden md:flex" : ""
                            }`}
                        >
                            <BrandMark />
                        </Link>
                    </div>

                    {/* CENTERED brand on mobile sub-pages */}
                    {!isHome && (
                        <Link
                            to="/"
                            data-testid="nav-wordmark-centered"
                            aria-label="The Gaffer — Home"
                            className="md:hidden absolute left-1/2 -translate-x-1/2"
                        >
                            <BrandMark size="sm" />
                        </Link>
                    )}

                    {/* DESKTOP nav links (absolute-centered) */}
                    <nav className="hidden md:flex items-center gap-8 lg:gap-10 absolute left-1/2 -translate-x-1/2">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                onClick={handleNavClick}
                                data-testid={`nav-link-${l.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `uppercase tracking-[0.18em] text-[0.78rem] font-extrabold transition-colors duration-300 ${
                                        isActive
                                            ? "text-[#EB4A18] underline underline-offset-[10px] decoration-2 decoration-[#EB4A18]"
                                            : "text-[#121212] hover:text-[#EB4A18] no-underline"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>

                    {/* RIGHT — desktop Order CTA + mobile hamburger */}
                    <div className="flex items-center gap-2">
                        <a
                            href={ORDER_ONLINE_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="nav-order-online-btn"
                            className="!hidden md:!inline-flex btn-primary text-[0.78rem] py-3.5 px-7 whitespace-nowrap"
                        >
                            Order Pickup
                            <span aria-hidden="true">→</span>
                        </a>
                        <button
                            type="button"
                            onClick={() => setOpen((v) => !v)}
                            data-testid="nav-mobile-toggle"
                            aria-label={open ? "Close menu" : "Open menu"}
                            aria-expanded={open}
                            className="md:hidden -mr-2 p-2 text-[#121212] hover:text-[#EB4A18] transition-colors duration-200"
                        >
                            <svg
                                aria-hidden="true"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.4"
                                strokeLinecap="round"
                                className="w-10 h-10"
                            >
                                {open ? (
                                    <path d="M6 6l12 12M18 6L6 18" />
                                ) : (
                                    <>
                                        <path d="M3 6h18" />
                                        <path d="M3 12h18" />
                                        <path d="M3 18h18" />
                                    </>
                                )}
                            </svg>
                        </button>
                    </div>
                </div>
            </header>

            {/* MOBILE OVERLAY MENU */}
            {open && (
                <div
                    data-testid="nav-mobile-panel"
                    className="md:hidden fixed inset-0 z-40 bg-[#FAF8F4] flex flex-col"
                >
                    <div className="h-20" />
                    <nav className="flex-1 px-6 pt-8 flex flex-col gap-2">
                        {links.map((l, idx) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                onClick={handleNavClick}
                                className={({ isActive }) =>
                                    `font-display uppercase text-5xl py-3 border-b border-black/10 transition-colors duration-200 no-underline ${
                                        isActive
                                            ? "text-[#EB4A18]"
                                            : "text-[#121212]"
                                    }`
                                }
                                style={{ animationDelay: `${idx * 50}ms` }}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="px-6 pb-10 pt-6 space-y-4">
                        {/* Check voucher balance — small link before admin */}
                        <Link
                            to="/vouchers/balance"
                            onClick={() => setOpen(false)}
                            data-testid="nav-mobile-check-balance-link"
                            className="overline text-[0.7rem] text-[#121212] hover:text-[#EB4A18] tracking-[0.28em] inline-flex items-center gap-2"
                        >
                            Check Voucher Balance
                            <span aria-hidden="true">→</span>
                        </Link>
                        {/* Owner Dashboard — discreet link at the very bottom */}
                        <div className="pt-6 mt-2 border-t border-black/10">
                            <Link
                                to="/admin"
                                onClick={() => setOpen(false)}
                                data-testid="nav-mobile-admin-link"
                                className="overline text-[0.6rem] text-[#6B6B6B] hover:text-[#EB4A18] tracking-[0.3em] inline-flex items-center gap-2"
                            >
                                Owner Dashboard
                                <span aria-hidden="true">→</span>
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
